import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { isEmpty, pickBy } from 'lodash';
import { useBoundStore } from 'store';
import { setDraftPreviewMode, setMode } from 'store/DocumentMode';
import { resetItemsToPrint, useItemsToPrint, useShouldPrintAll } from 'store/DocumentPrint';
import { setDocumentMode, useDocumentMode } from 'store/Layout';
import { setRenvoiMode } from 'store/DocumentSearch';
import { routes } from 'config';
import { LocalizationContext, UserContext } from 'contexts';
import { DocumentMode } from 'types/Document';
import { addQuery, parseQuery } from 'helpers/query';
import { RenvoiMode } from 'clients/documents/documentsClient.types';
import { checkAllRelatedPrintCheckboxes } from 'modules/Document/helpers';
import { Document } from 'views/LoggedIn/Document/Document';
import { Document as PublicDocument } from 'views/LoggedOut/Document';

type Props = {
	open: boolean,
	onClose: () => void,
	options?: {
		showVersionFilters?: boolean;
	};
}

export const EXPORT_FORMATS = {
  PRINT: 'print',
  DOCX: 'docx',
};

export const getRenvoiModeSelectLabel = (mode: RenvoiMode, dictionary: any): string => {
  const modeLabel = (dictionary as any)?.renvoiMode?.[mode];

  if (typeof modeLabel === 'object' && modeLabel !== null) {
    return modeLabel.label || modeLabel.title || String(mode);
  }

  return typeof modeLabel === 'string' ? modeLabel : String(mode);
};

export const PrintDialog: FC<Props> = ({ open, onClose, options }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoggedIn } = useContext(UserContext);
  const { dictionary } = useContext(LocalizationContext);
  const documentMode = useDocumentMode();
  const renvoiMode = useBoundStore((state) => state.renvoiMode);

  const itemsToPrint = useItemsToPrint();
  const shouldPrintAll = useShouldPrintAll();

  const [format, setFormat] = useState(EXPORT_FORMATS.DOCX);

  const selectedItems = useMemo(() => {
    return pickBy(itemsToPrint);
  }, [itemsToPrint]);

  const onPrintAllChange = useCallback((isChecked: boolean) => {
    const itemsToUpdate = checkAllRelatedPrintCheckboxes('', isChecked);

    useBoundStore.setState({
      shouldPrintAll: isChecked,
      itemsToPrint: itemsToUpdate,
    });
  }, []);

  useEffect(() => {
    if (open) {
      useBoundStore.setState({
        shouldPrintAll: true,
        itemsToPrint: {},
      });
    } else {
      resetItemsToPrint();
    }
  }, [open]);

  const handleClose = () => {
    setDocumentMode(DocumentMode.Read);
    setMode(DocumentMode.Read);
    setDraftPreviewMode(false);
    onClose();
  };

  const shouldShowButton = shouldPrintAll || !isEmpty(selectedItems);

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={handleClose}
      className="print-dialog"
    >
      <Grid
        container
        display="flex"
        sx={{
          bgcolor: '#E5E5E5',
        }}
      >
        <Grid item xs={8}>
          <Box
            sx={{
              overflowY: 'auto',
              height: '90vh',
            }}
            className={shouldPrintAll ? 'print-mode--hide-checkboxes' : ''}
          >
            {isLoggedIn ? (
              <Document isPrintMode mode={documentMode}/>
            ) : (
              <PublicDocument isPrintMode mode={documentMode}/>
            )}
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box
            sx={{
              height: '100%',
              p: 2,
            }}
          >
            <Stack justifyContent="space-between" height="100%">
              <Box>
                <Typography variant="h6">{dictionary.printDialog.title}</Typography>
                <FormControlLabel
                  label={dictionary.printDialog.form.entireDocument}
                  control={
                    <Checkbox
                      checked={shouldPrintAll}
                      onChange={(event) => {
                        onPrintAllChange(event.target.checked);
                      }}
                    />
                  }
                />
                {options?.showVersionFilters && (
                  <TextField
                    style={{ minWidth: 200, marginTop: 32, marginBottom: '12px' }}
                    size="small"
                    label={dictionary.printDialog.form.version}
                    defaultValue={documentMode}
                    fullWidth
                    onChange={(event) => setDocumentMode(event.target.value as DocumentMode)}
                    select
                  >
                    <MenuItem sx={{ p: 2 }} value={DocumentMode.Read} key="read">
                      {dictionary.documentStatuses.read}
                    </MenuItem>
                    <MenuItem sx={{ p: 2 }} value={DocumentMode.Write} key="write">
                      {dictionary.documentStatuses.write}
                    </MenuItem>
                  </TextField>
                )}

                <TextField
                  style={{ minWidth: 200, marginTop: 16, marginBottom: '12px' }}
                  size="small"
                  label={(dictionary.printDialog?.form as any)?.renvoiModeLabel || 'Renvoi Mode'}
                  value={renvoiMode}
                  fullWidth
                  onChange={(event) => {
                    setRenvoiMode(event.target.value as RenvoiMode);
                  }}
                  select
                >
                  {Object.values(RenvoiMode).map((mode) => (
                    <MenuItem sx={{ p: 2 }} value={mode} key={mode}>
                      {getRenvoiModeSelectLabel(mode as RenvoiMode, dictionary)}
                    </MenuItem>
                  ))}
                </TextField>

                {location.pathname === routes.document && (
                  <Box>
                    <ToggleButtonGroup
                      size="small"
                      value={format}
                      exclusive
                      onChange={(event, newFormat) => {
                        if (newFormat) setFormat(newFormat);
                      }}
                    >
                      <ToggleButton
                        value={EXPORT_FORMATS.DOCX}
                        key={EXPORT_FORMATS.DOCX}
                        sx={{
                          padding: '6px 24px',
                        }}
                      >
														DOCX
                      </ToggleButton>
                      <ToggleButton
                        value={EXPORT_FORMATS.PRINT}
                        key={EXPORT_FORMATS.PRINT}
                        sx={{
                          padding: '6px 24px',
                        }}
                      >
                        {dictionary.printDialog.format.print}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
              </Box>

              <Box display="flex" justifyContent="right">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!shouldShowButton}
                  onClick={() => {
                    navigate({
                      pathname: routes.documentPrintPreview,
                      search: addQuery('', {
                        slug: parseQuery(location.search).slug,
                        'draft-secret': parseQuery(location.search)['draft-secret'],
                        'important-only': parseQuery(location.search)['important-only'],
                        'section-id': parseQuery(location.search)['section-id'],
                        'format': format,
                        'mode': documentMode,
                        'renvoi-mode': renvoiMode,
                      }),
                    });
                    onClose();
                  }}
                >
                  {shouldShowButton ? (
                    <>
                      {shouldPrintAll && format === EXPORT_FORMATS.DOCX && dictionary.printDialog.form.buttonLabels.docxFull}
                      {!shouldPrintAll && format === EXPORT_FORMATS.DOCX && dictionary.printDialog.form.buttonLabels.docxPartial}
                      {shouldPrintAll && format === EXPORT_FORMATS.PRINT && dictionary.printDialog.form.buttonLabels.printFull}
                      {!shouldPrintAll && format === EXPORT_FORMATS.PRINT && dictionary.printDialog.form.buttonLabels.printPartial}
                    </>
                  ) : (
                    <>
                      {dictionary.printDialog.form.buttonLabels.docxFull}
                    </>
                  )}
                </Button>
              </Box>


            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
};